/**
 * UsageBeacon — first-party web usage (route views), batched to
 * recordUsageEvents. Mirrors the mobile analytics module: fail-soft,
 * signed-in only, no third-party vendor. Renders nothing.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { initFirebaseClient } from '@copapp/firebase/client';
import { useAuth } from '../../contexts/AuthContext';

const FLUSH_MS = 30_000;
const FLUSH_AT = 20;

export function UsageBeacon() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const queue = useRef<Array<{ name: string; screen: string; ts: string }>>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (queue.current.length === 0) return;
    const batch = queue.current.splice(0, 50);
    try {
      const { functions } = initFirebaseClient();
      void httpsCallable(functions, 'recordUsageEvents')({
        events: batch,
        context: { platform: 'web' },
      }).catch(() => undefined);
    } catch { /* fail-soft */ }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    // route → screen name: '/sim' → 'sim', '/' → 'landing'
    const screen = (location.pathname.split('/')[1] || 'landing').slice(0, 40);
    queue.current.push({ name: 'screen_view', screen, ts: new Date().toISOString() });
    if (queue.current.length >= FLUSH_AT) flush();
    else if (!timer.current) timer.current = setTimeout(flush, FLUSH_MS);
  }, [location.pathname, isAuthenticated]);

  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, []);

  return null;
}
